import React from 'react';
import { AlertCircle, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'components/ui/panel';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import Badge from 'components/ui/badge';
import { scoreTextClass } from 'components/ui/status-badge';
import type { ItemFlag, RiskBand } from 'modules/analytics';

export const axisTick = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' } as const;
export const gridStroke = 'hsl(var(--border))';

/* One tooltip shared by every analytics chart. Recharts injects `active`/`payload`/`label`. */

export interface ChartTooltipEntry {
  name?: string;
  value?: number | string;
  color?: string;
  fill?: string;
  dataKey?: string | number;
  payload?: Record<string, unknown>;
}

export interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: ChartTooltipEntry[];
  /** Format a numeric series value. Receives the entry for per-series logic. */
  valueFormatter?: (value: number, name: string, entry: ChartTooltipEntry) => React.ReactNode;
  /** Format the axis label shown as the tooltip heading. */
  labelFormatter?: (label: string | number) => React.ReactNode;
  /** Derive a heading from the hovered datum (e.g. scatter point label). */
  resolveLabel?: (payload: ChartTooltipEntry[]) => React.ReactNode;
}

/** The dark chart tooltip: a light heading over the bold figure. */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  label,
  payload,
  valueFormatter,
  labelFormatter,
  resolveLabel,
}) => {
  if (!active || !payload || payload.length === 0) return null;

  const heading = resolveLabel
    ? resolveLabel(payload)
    : labelFormatter
      ? labelFormatter(label ?? '')
      : label;

  return (
    <div className="flex flex-col gap-px rounded-md bg-foreground px-2 py-1.5 text-[11.5px] leading-tight text-background">
      {heading != null && heading !== '' && <span className="text-background/70">{heading}</span>}
      {payload.map((entry, i) => {
        const numeric = typeof entry.value === 'number' ? entry.value : Number(entry.value);
        const display =
          valueFormatter && !Number.isNaN(numeric)
            ? valueFormatter(numeric, entry.name ?? '', entry)
            : typeof entry.value === 'number'
              ? entry.value.toLocaleString()
              : entry.value;
        return (
          <span key={`${entry.dataKey ?? entry.name ?? i}`} className="flex items-baseline gap-2">
            {entry.name && <span className="text-background/70">{entry.name}</span>}
            <span className="ml-auto font-semibold tabular-nums">{display}</span>
          </span>
        );
      })}
    </div>
  );
};

interface SectionCardProps {
  title: string;
  description?: string;
  right?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** A Panel whose body may open with one muted line describing the figure. */
export const SectionCard: React.FC<SectionCardProps> = ({ title, description, right, className, children }) => (
  <Panel title={title} aside={right} className={className}>
    {description && <p className="-mt-1 mb-3 text-[12.5px] text-muted-foreground">{description}</p>}
    {children}
  </Panel>
);

export const LoadingBlock: React.FC<{ className?: string }> = ({ className }) => (
  <div data-analytics-loading className={className}>
    <Skeleton className="w-full" height={160} />
  </div>
);

export const ErrorBlock: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<AlertCircle />}
      title={t('analytics_error_load')}
      description={message ?? t('please_try_again_later')}
    />
  );
};

export const EmptyBlock: React.FC<{ message: string }> = ({ message }) => (
  <EmptyState icon={<BarChart3 />} title={message} />
);

/** Score text colour in the app-wide bands (≥78 · 60–77 · <60). */
export function scoreColorClass(pct: number): string {
  return scoreTextClass(pct);
}

const RISK_VARIANT: Record<RiskBand, 'success' | 'warning' | 'danger'> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
};

export const RiskBadge: React.FC<{ band: RiskBand; score: number }> = ({ band, score }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={RISK_VARIANT[band]} size="sm">
      {t(`analytics_risk_${band}`)} · {score}
    </Badge>
  );
};

const FLAG_VARIANT: Record<NonNullable<ItemFlag>, 'warning' | 'danger'> = {
  too_easy: 'warning',
  too_hard: 'danger',
  poor_discrimination: 'warning',
};

export const ItemFlagBadge: React.FC<{ flag: ItemFlag }> = ({ flag }) => {
  const { t } = useTranslation();
  if (!flag) return null;
  return (
    <Badge variant={FLAG_VARIANT[flag]} size="sm">
      {t(`analytics_items_${flag}`)}
    </Badge>
  );
};
